import { LoadingButton } from '@mui/lab'
import {
  Card,
  CardContent,
  Checkbox,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import { GreenText, OrderableTableCell } from 'components'
import { moveToInvoicingServices } from 'controllers/invoicing/index'
import { InvoicingDto, VehicleEligibleForInvoicingDto } from 'dtos/invoicing'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { clientSideOrderBy, clientSidePagination } from 'utils'

interface VehiclesEligibleForInvoicingCardProps {
  refreshInvoicing: (invoicing: InvoicingDto) => void
  vehiclesEligibleForInvoicing: VehicleEligibleForInvoicingDto[]
}

export default function VehiclesEligibleForInvoicingCard({ refreshInvoicing, vehiclesEligibleForInvoicing }: VehiclesEligibleForInvoicingCardProps) {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<keyof VehicleEligibleForInvoicingDto>('importerStockNumber')
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)
  const [selected, setSelected] = useState<VehicleEligibleForInvoicingDto[]>([])

  const onChangeSortByDirection = (propertyName: keyof VehicleEligibleForInvoicingDto) => {
    setOrder(orderBy === propertyName && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(propertyName)
  }

  useEffect(() => {
    setPage(0)
  }, [pageSize])

  return (
    <Card>
      <CardContent>
        <Grid container spacing={1} alignItems='center'>
          <Grid item xs={12}>
            <Typography fontWeight='bold' variant='h6'>
              Vehicles Eligible For Invoicing
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableCell padding='checkbox'></TableCell>

                  <OrderableTableCell<VehicleEligibleForInvoicingDto>
                    keyName='importerStockNumber'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    Importer Stock #
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleEligibleForInvoicingDto>
                    keyName='buyerExporterCompany'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    Buyer/Exporter
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleEligibleForInvoicingDto>
                    keyName='papsNumber'
                    onChangeOrder={onChangeSortByDirection}
                    order={order}
                    orderBy={orderBy}
                  >
                    PAPS #
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleEligibleForInvoicingDto> keyName='vin' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    VIN
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleEligibleForInvoicingDto> keyName='year' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    Year
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleEligibleForInvoicingDto> keyName='make' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    Make
                  </OrderableTableCell>

                  <OrderableTableCell<VehicleEligibleForInvoicingDto> keyName='model' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                    Model
                  </OrderableTableCell>
                </TableHead>

                <TableBody>
                  {vehiclesEligibleForInvoicing
                    ?.sort(clientSideOrderBy<VehicleEligibleForInvoicingDto>(orderBy, order))
                    .slice(...clientSidePagination(page, pageSize))
                    .map((vehicle, index) => (
                      <TableRow key={vehicle.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                        <TableCell padding='checkbox'>
                          <Checkbox
                            checked={selected.some(value => value.vehicleGuid === vehicle.vehicleGuid)}
                            disabled={isSubmitting}
                            onChange={() => {
                              if (selected.some(value => value.vehicleGuid === vehicle.vehicleGuid)) {
                                setSelected(selected.filter(value => value.vehicleGuid !== vehicle.vehicleGuid))
                              } else {
                                setSelected([...selected, vehicle])
                              }
                            }}
                          />
                        </TableCell>

                        <TableCell>{vehicle.importerStockNumber}</TableCell>

                        <TableCell>{vehicle.buyerExporterCompany}</TableCell>

                        <TableCell>{vehicle.papsNumber}</TableCell>

                        <TableCell>{vehicle.vin}</TableCell>

                        <TableCell>{vehicle.year}</TableCell>

                        <TableCell>{vehicle.make}</TableCell>

                        <TableCell>{vehicle.model}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      count={vehiclesEligibleForInvoicing.length ?? 0}
                      onPageChange={(_, page) => setPage(page)}
                      onRowsPerPageChange={e => setPageSize(e.target.value as unknown as number)}
                      page={page}
                      rowsPerPage={pageSize}
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      SelectProps={{ fullWidth: false }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Vehicles Eligible For Invoicing:**

This list displays vehicles where the "Sent To DOT" field under Vehicle Details → Conformity (tab) on the Conformity Details card contains a date.

**Move Selected Vehicles To Invoicing Services:**

Moves selected vehicles from the Vehicles Eligible For Invoicing list to the Invoicing Services list.`}
            </GreenText>
          </Grid>

          <Grid item container alignItems='center' justifyContent='flex-end'>
            <Grid item>
              <LoadingButton
                disabled={selected.length === 0}
                loading={isSubmitting}
                onClick={() => {
                  setIsSubmitting(true)
                  moveToInvoicingServices(selected)
                    .then(res => {
                      enqueueSnackbar('Vehicles Moved To Invoicing Services Successfully!', { variant: 'success' })
                      refreshInvoicing(res.value)
                      setSelected([])
                      setPage(0)
                    })
                    .finally(() => setIsSubmitting(false))
                }}
                variant='contained'
              >
                MOVE SELECTED VEHICLES TO INVOICING SERVICES
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
